import React, { useEffect, useMemo, useState } from "react";
import { Offcanvas, Form, Button } from "react-bootstrap";
import Select, { components, type ValueContainerProps } from "react-select";
import { useScopeTypes } from "../../../hooks/useScopeTypes";
import { useRoleOptions } from "../../../hooks/useRoleOptions";
import { useScopeIds } from "../../../hooks/useScopeIds";
import { useDebounce } from "../../../hooks/useDebounce";
import { useAddUserDrawer } from "./useAddUserDrawer";
import "./AddUserDrawer.css";

type ScopeIdOption = { label: string; value: string };

type ViewAssignment = {
  roleCode: string;
  scopeType: string;
  scopeIds: string[];
  academyId?: string;
};

const ViewAssignmentBlock: React.FC<{
  assignment: ViewAssignment;
  roleOptions: { label: string; value: string }[];
  scopeTypeOptions: { label: string; value: string }[];
}> = ({ assignment, roleOptions, scopeTypeOptions }) => {
  const { scopeIdOptions } = useScopeIds(assignment.scopeType);

  const mergedViewScopeOptions = useMemo((): ScopeIdOption[] => {
    const byValue = new Map<string, ScopeIdOption>();
    for (const opt of scopeIdOptions) {
      byValue.set(opt.value, opt);
    }
    for (const id of assignment.scopeIds) {
      if (!id || byValue.has(id)) continue;
      byValue.set(id, { value: id, label: id });
    }
    return Array.from(byValue.values());
  }, [scopeIdOptions, assignment.scopeIds]);

  const viewScopeIdValue = useMemo((): ScopeIdOption[] => {
    return assignment.scopeIds
      .map((id) => mergedViewScopeOptions.find((o) => o.value === id))
      .filter((o): o is ScopeIdOption => Boolean(o));
  }, [assignment.scopeIds, mergedViewScopeOptions]);

  return (
    <div className="assign-role-block">
      <div className="assign-role-block-header">
        <span className="assign-role-block-title">
          {assignment.roleCode || "Role"}
        </span>
      </div>
      <div className="assign-role-field-row two-column-row">
        <Form.Group className="assign-role-field" style={{ flex: 1 }}>
          <Form.Label>Role</Form.Label>
          <Select
            classNamePrefix="react-select"
            value={
              roleOptions.find((opt) => opt.value === assignment.roleCode) ??
              (assignment.roleCode
                ? { label: assignment.roleCode, value: assignment.roleCode }
                : null)
            }
            options={roleOptions}
            isDisabled
            placeholder="—"
            menuPortalTarget={document.body}
          />
        </Form.Group>
        <Form.Group className="assign-role-field" style={{ flex: 1 }}>
          <Form.Label>Scope Type</Form.Label>
          <Select
            classNamePrefix="react-select"
            value={
              assignment.scopeType
                ? scopeTypeOptions.find((opt) => opt.value === assignment.scopeType) ??
                  { label: assignment.scopeType, value: assignment.scopeType }
                : null
            }
            options={scopeTypeOptions}
            isDisabled
            placeholder="—"
            isClearable={false}
            menuPortalTarget={document.body}
          />
        </Form.Group>
      </div>
      <div className="assign-role-field assign-role-field-row">
        <div style={{ flex: 1 }}>
          <Form.Label>Scope ID(s)</Form.Label>
          <Select
            classNamePrefix="react-select"
            isMulti
            components={{
              ValueContainer: ScopeIdValueContainer,
              MultiValue: () => null,
            }}
            hideSelectedOptions={false}
            value={viewScopeIdValue}
            options={mergedViewScopeOptions}
            isDisabled
            placeholder="—"
            menuPortalTarget={document.body}
          />
        </div>
      </div>
    </div>
  );
};

const ScopeIdValueContainer = (props: ValueContainerProps<ScopeIdOption, true>) => {
  const { getValue, hasValue, children } = props;
  const values = getValue();

  if (!hasValue || !Array.isArray(values) || values.length === 0) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  const first = values[0];
  const extraCount = values.length - 1;

  return (
    <components.ValueContainer {...props}>
      <span>{first.label}</span>
      {extraCount > 0 && (
        <span className="add-user-multi-summary"> ... +{extraCount}</span>
      )}
      {children}
    </components.ValueContainer>
  );
};

export interface UserFormData {
  userId?: string;
  name: string;
  email: string;
  phone?: string;
  countryCode?: number;
  status?: string;
  roles: string[];
  initialScope?: string;
  scopeType?: string;
  scopeId?: string;
  scopeIds?: string[];
  // Optional raw assignments for this user (used by AssignRoleModal)
  assignments?: {
    assignmentId?: string;
    roleCode: string;
    scopeType?: string;
    scopeIds?: string[];
  }[];
}

export type AddUserDrawerMode = "create" | "edit" | "view";

interface AddUserDrawerProps {
  show: boolean;
  onHide: () => void;
  mode?: AddUserDrawerMode;
  initialData?: UserFormData;
  onSuccess?: () => void;
}

const AddUserDrawer: React.FC<AddUserDrawerProps> = ({
  show,
  onHide,
  mode = "create",
  initialData,
  onSuccess,
}) => {
  const { scopeTypeOptions } = useScopeTypes();
  const [roleSearch, setRoleSearch] = useState("");
  const [scopeIdSearch, setScopeIdSearch] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const debouncedRoleSearch = useDebounce(roleSearch, 600);
  const debouncedScopeIdSearch = useDebounce(scopeIdSearch, 600);
  const { roleOptions, loading: rolesLoading } = useRoleOptions(
    debouncedRoleSearch,
    show
  );
  const { functions, values, loading } = useAddUserDrawer({
    show,
    onHide,
    mode,
    initialData,
    onSuccess,
  });

  const { scopeIdOptions } = useScopeIds(
    values.scopeType,
    undefined,
    debouncedScopeIdSearch
  );

  const [persistedScopeIdLabels, setPersistedScopeIdLabels] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!scopeIdOptions.length) return;
    setPersistedScopeIdLabels((prev) => {
      const next = { ...prev };
      for (const opt of scopeIdOptions) {
        if (values.scopeIds.includes(opt.value)) {
          next[opt.value] = opt.label;
        }
      }
      return next;
    });
  }, [scopeIdOptions, values.scopeIds]);

  useEffect(() => {
    setPersistedScopeIdLabels((prev) => {
      const next: Record<string, string> = {};
      for (const id of values.scopeIds) {
        if (id && prev[id]) next[id] = prev[id];
      }
      return next;
    });
  }, [values.scopeIds, values.scopeType]);

  const mergedScopeIdOptions = useMemo((): ScopeIdOption[] => {
    const labels: Record<string, string> = { ...persistedScopeIdLabels };
    for (const opt of scopeIdOptions) {
      if (values.scopeIds.includes(opt.value)) {
        labels[opt.value] = opt.label;
      }
    }
    const byValue = new Map<string, ScopeIdOption>();
    for (const opt of scopeIdOptions) {
      byValue.set(opt.value, opt);
    }
    for (const id of values.scopeIds) {
      if (!id || byValue.has(id)) continue;
      byValue.set(id, { value: id, label: labels[id] || id });
    }
    return Array.from(byValue.values());
  }, [scopeIdOptions, values.scopeIds, persistedScopeIdLabels]);

  const scopeIdSelectValue = useMemo((): ScopeIdOption[] => {
    return values.scopeIds
      .map((id) => mergedScopeIdOptions.find((o) => o.value === id))
      .filter((o): o is ScopeIdOption => Boolean(o));
  }, [values.scopeIds, mergedScopeIdOptions]);

  const viewAssignments: ViewAssignment[] = (() => {
    if (!initialData) return [];
    if (Array.isArray(initialData.assignments) && initialData.assignments.length > 0) {
      return initialData.assignments
        .filter((a) => a && typeof a.roleCode === "string" && a.roleCode.length > 0)
        .map((a) => ({
          roleCode: a.roleCode,
          scopeType: a.scopeType ?? "",
          scopeIds: a.scopeIds ?? [],
          academyId: undefined,
        }));
    }

    const scopeTypeFallback = initialData.scopeType ?? initialData.initialScope ?? "";
    const scopeIdsFallback =
      initialData.scopeIds ?? (initialData.scopeId ? [initialData.scopeId] : []);
    const uniqueRoles = Array.from(new Set((initialData.roles ?? []).filter(Boolean)));
    return uniqueRoles.map((roleCode) => ({
      roleCode,
      scopeType: scopeTypeFallback,
      scopeIds: scopeIdsFallback,
      academyId: undefined,
    }));
  })();

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="add-user-drawer"
      style={{ width: 640, maxWidth: "100%" }}
    >
      <div className="add-user-drawer-header">
        <h2 className="add-user-drawer-title">{values.drawerTitle}</h2>
        <button
          type="button"
          className="add-user-drawer-close btn-close"
          onClick={onHide}
          aria-label="Close"
        />
      </div>
      <Offcanvas.Body className="add-user-drawer-body">
        <div className="add-user-form">
          <div className="add-user-fields">
            <div className="add-user-fields-row">
              <Form.Group className="add-user-field">
                <Form.Label>
                  Full Name {!values.isView && <span className="required">*</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={values.fullName}
                  onChange={(e) => functions.handleFullNameChange(e.target.value)}
                  placeholder="Enter full name"
                  className="add-user-input"
                  readOnly={!!values.isView}
                  tabIndex={values.isView ? -1 : 0}
                  autoComplete="new-password"
                  name="fullName_no_autocomplete"
                />
              </Form.Group>
              <Form.Group className="add-user-field">
                <Form.Label>
                  Email {!values.isView && <span className="required">*</span>}
                </Form.Label>
                <Form.Control
                  type="email"
                  value={values.email}
                  onChange={(e) => functions.handleEmailChange(e.target.value)}
                  placeholder="Enter email address"
                  className="add-user-input"
                  readOnly={!!values.isView}
                  tabIndex={values.isView ? -1 : 0}
                  autoComplete="off"
                />
              </Form.Group>
            </div>
            <div className="add-user-fields-row">
              <div className="add-user-half-row">
                {((!values.isView) ||
                  (!!values.phone && !values.isView) ||
                  (!!values.countryCode && !values.isView)) && (
                  <Form.Group className="add-user-field add-user-field-country-code">
                    <Form.Label>Country Code</Form.Label>
                    <Form.Control
                      type="number"
                      value={values.countryCode ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        functions.handleCountryCodeChange(
                          v === "" ? undefined : parseInt(v, 10)
                        );
                      }}
                      placeholder="e.g. 91"
                      className="add-user-input"
                      readOnly={!!values.isView}
                    />
                  </Form.Group>
                )}
                {((!values.isView) ||
                  (!!values.phone && !values.isView)) && (
                  <Form.Group className="add-user-field">
                    <Form.Label>
                      Phone Number
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      value={values.phone}
                      onChange={(e) => functions.handlePhoneChange(e.target.value)}
                      placeholder="Enter phone number"
                      className="add-user-input"
                      readOnly={!!values.isView}
                      autoComplete="off"
                    />
                    {values.phoneError && (
                      <div className="text-danger small mt-1">
                        {values.phoneError}
                      </div>
                    )}
                  </Form.Group>
                )}
              </div>
              {!values.isView && (
                <Form.Group className="add-user-field add-user-field-half">
                  <Form.Label>
                    {values.isEdit ? "Reset Password" : "Password"}
                    {!values.isEdit && <span className="required">*</span>}
                  </Form.Label>
                  <div className="add-user-password-wrapper">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      value={values.password}
                      onChange={(e) => functions.handlePasswordChange(e.target.value)}
                      placeholder="Min 8 characters"
                      className="add-user-input add-user-input-password"
                      minLength={8}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="add-user-password-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                    </button>
                  </div>
                </Form.Group>
              )}
            </div>
          </div>

          {values.isView && (
            <div className="add-user-assign-role">
              <h3 className="add-user-assign-role-title">Assigned Role(s)</h3>
              <div className="add-user-assign-role-fields">
                {viewAssignments.length === 0 ? (
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    —
                  </div>
                ) : (
                  <div className="assign-role-modal-fields">
                    {viewAssignments.map((assignment, idx) => (
                      <ViewAssignmentBlock
                        key={`${assignment.roleCode}-${idx}`}
                        assignment={assignment}
                        roleOptions={roleOptions}
                        scopeTypeOptions={scopeTypeOptions}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Show Assign Role block only when creating a new user.
              In edit mode, only user details should be editable. */}
          {!values.isEdit && !values.isView && (
            <div className="add-user-assign-role">
              <h3 className="add-user-assign-role-title">Assign Role</h3>
              <div className="add-user-assign-role-fields">
                <div className="add-user-fields-row">
                  <Form.Group className="add-user-field">
                    <Form.Label>
                      Select Role <span className="required">*</span>
                    </Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      value={
                        roleOptions.find((opt) => opt.value === values.role) ?? null
                      }
                      onChange={(option) =>
                        functions.handleRoleChange(option?.value ?? "")
                      }
                      options={roleOptions}
                      placeholder="Select Role"
                      isLoading={rolesLoading}
                      onInputChange={(input, meta) => {
                        if (meta.action === "input-change") {
                          setRoleSearch(input);
                        }
                      }}
                      filterOption={null}
                      isDisabled={!!values.isView}
                      menuPortalTarget={document.body}
                    />
                  </Form.Group>
                  <Form.Group className="add-user-field">
                    <Form.Label>
                      Select Scope Type
                    </Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      value={
                        values.scopeType
                          ? scopeTypeOptions.find(
                              (opt) => opt.value === values.scopeType
                            ) ?? null
                          : null
                      }
                      onChange={(option) =>
                        functions.handleScopeTypeChange(option?.value ?? "")
                      }
                      options={scopeTypeOptions}
                      isDisabled={!!values.isView}
                      placeholder="Select scope type"
                      isClearable
                      menuPortalTarget={document.body}
                    />
                  </Form.Group>
                </div>
                <div className="add-user-fields-row">
                  <Form.Group className="add-user-field add-user-field-half">
                    <Form.Label>
                      Select Scope ID(s)
                    </Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      isMulti
                      components={{
                        ValueContainer: ScopeIdValueContainer,
                        MultiValue: () => null,
                      }}
                      hideSelectedOptions={false}
                      value={scopeIdSelectValue}
                      onChange={(selected) =>
                        {
                          const opts = selected ?? [];
                          setPersistedScopeIdLabels((prev) => {
                            const next: Record<string, string> = { ...prev };
                            for (const opt of opts) {
                              next[opt.value] = opt.label;
                            }
                            for (const key of Object.keys(next)) {
                              if (!opts.some((o) => o.value === key)) {
                                delete next[key];
                              }
                            }
                            return next;
                          });
                          functions.handleScopeIdsChange(
                            opts.map((o) => o.value)
                          );
                          setScopeIdSearch("");
                        }
                      }
                      options={mergedScopeIdOptions}
                      isDisabled={!!values.isView}
                      placeholder="Select scope ID(s)"
                      isClearable
                      menuPortalTarget={document.body}
                      onInputChange={(input, meta) => {
                        if (meta.action === "input-change") {
                          setScopeIdSearch(input);
                        }
                      }}
                      onMenuClose={() => setScopeIdSearch("")}
                      onBlur={() => setScopeIdSearch("")}
                      filterOption={null}
                    />
                  </Form.Group>
                </div>
              </div>
            </div>
          )}
        </div>
      </Offcanvas.Body>
      {!values.isView && (
        <div className="add-user-drawer-footer">
          {values.submitError && (
            <div className="text-danger small mb-2">{values.submitError}</div>
          )}
          <Button
            variant="outline-dark"
            className="add-user-btn add-user-btn-cancel"
            onClick={onHide}
            disabled={loading.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="add-user-btn add-user-btn-submit"
            onClick={() => functions.handleSubmit()}
            disabled={loading.isSubmitting}
          >
            {loading.isSubmitting ? "Creating…" : values.isEdit ? "Update User" : "Confirm"}
          </Button>
        </div>
      )}
    </Offcanvas>
  );
};

export default AddUserDrawer;
